import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { NotificationPreferences } from '../components/NotificationPreferences';
import { CallCountCard } from '../components/CallCountCard';
import { ArrowLeft, Save, Stethoscope, BellRing } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DoctorProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    full_name: '',
    phone_number: '',
    specialty: '',
    biography: '',
    profile_picture: '',
    category: 'doctors'
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || '',
        phone_number: user.phone_number || '',
        specialty: user.specialty || '',
        biography: user.biography || '',
        profile_picture: user.profile_picture || user.profile_photo || '',
        category: user.category || 'doctors'
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg({ type: '', text: '' });
    try {
      await axios.put(`${API}/users/profile`, form, { withCredentials: true });
      await checkAuth();
      setMsg({ type: 'success', text: t('تم حفظ التغييرات بنجاح', 'Profile updated successfully', 'Profil mis à jour') });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || t('حدث خطأ', 'An error occurred', 'Erreur') });
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setMsg({ type: 'error', text: t('الصورة كبيرة جداً (الحد الأقصى 2 ميجا)', 'Image too large (max 2MB)', 'Image trop grande (max 2MB)') });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, profile_picture: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div className={`min-h-screen bg-gray-50 py-8 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4 max-w-3xl">
        <Button variant="ghost" onClick={() => navigate('/doctor')} className="mb-4" data-testid="back-to-dashboard">
          <ArrowLeft className={language === 'ar' ? 'ml-2 rotate-180' : 'mr-2'} size={20} />
          {t('العودة', 'Back', 'Retour')}
        </Button>

        <Card className="p-6 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-[#2c3e50] mb-6" data-testid="doctor-profile-title">
            {t('الملف الشخصي للطبيب', 'Doctor Profile', 'Profil du médecin')}
          </h1>

          {msg.text && (
            <Alert variant={msg.type === 'error' ? 'destructive' : 'default'} className={`mb-4 ${msg.type === 'success' ? 'bg-green-50 border-green-200' : ''}`}>
              <AlertDescription className={msg.type === 'success' ? 'text-green-800' : ''}>{msg.text}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-start pb-4 border-b">
              <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden">
                {form.profile_picture ? (
                  <img src={form.profile_picture} alt="profile" className="w-full h-full object-cover" />
                ) : (
                  <Stethoscope size={40} className="text-[#1e88e5]" />
                )}
              </div>
              <div>
                <Label htmlFor="photo-upload" className="cursor-pointer">
                  <span className="inline-block px-4 py-2 bg-[#1e88e5] text-white rounded-md hover:bg-[#1565c0] text-sm">
                    {t('تحميل صورة', 'Upload Photo', 'Télécharger photo')}
                  </span>
                  <input id="photo-upload" type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload" />
                </Label>
                <p className="text-xs text-gray-500 mt-1">{t('PNG/JPG، أقصى 2 ميجا', 'PNG/JPG, max 2MB', 'PNG/JPG, max 2MB')}</p>
              </div>
            </div>

            <div>
              <Label>{t('الاسم الكامل', 'Full Name', 'Nom complet')}</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-2 h-12 text-base" data-testid="profile-name" required />
            </div>

            <div>
              <Label>{t('رقم الهاتف', 'Phone Number', 'Téléphone')}</Label>
              <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} className="mt-2 h-12 text-base" data-testid="profile-phone" />
            </div>

            <div>
              <Label>{t('التخصص', 'Specialty', 'Spécialité')}</Label>
              <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className="mt-2 h-12 text-base" data-testid="profile-specialty" />
            </div>

            <div>
              <Label>{t('السيرة الذاتية', 'Biography', 'Biographie')}</Label>
              <Textarea value={form.biography} onChange={(e) => setForm({ ...form, biography: e.target.value })} rows={4} className="mt-2 text-base" data-testid="profile-bio" />
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-[#1e88e5] hover:bg-[#1565c0]" data-testid="save-profile-button">
              <Save className={language === 'ar' ? 'ml-2' : 'mr-2'} size={18} />
              {loading ? t('جاري الحفظ...', 'Saving...', 'Enregistrement...') : t('حفظ التغييرات', 'Save Changes', 'Enregistrer')}
            </Button>
          </form>
        </Card>

        <div className="mt-6">
          <CallCountCard />
        </div>

        <Card className="p-4 sm:p-6 mt-6 bg-amber-50 border-amber-200">
          <h2 className="text-base md:text-lg font-bold text-amber-900 mb-1 flex items-center gap-2">
            <BellRing size={18} />
            {t('لكي تسمع رنين المكالمات', 'To hear incoming calls ring')}
          </h2>
          <p className="text-sm text-amber-800 leading-relaxed">
            {t(
              'أبقِ الإشعارات مفعّلة، ولا تضع الهاتف على الوضع الصامت، وثبّت التطبيق على الشاشة الرئيسية. عندما يتصل مريض ستظهر شاشة رنين كاملة، وإذا كان التطبيق مغلقاً سيصلك إشعار متكرر لمدة دقيقة.',
              'Keep notifications enabled, keep your phone off silent mode, and install the app on your home screen. When a patient calls you get a full-screen ring — and if the app is closed you get repeated alerts for a minute.',
              'Gardez les notifications activées et le téléphone hors mode silencieux.',
            )}
          </p>
          <a href="/install" className="text-sm font-bold text-[#0d47a1] underline mt-2 inline-block"
             data-testid="doctor-install-guide-link">
            {t('طريقة تثبيت التطبيق', 'How to install the app')}
          </a>
        </Card>

        <Card className="p-4 sm:p-6">
          <h2 className="text-xl font-bold text-[#2c3e50] mb-2">
            {t('كلمة المرور', 'Password', 'Mot de passe')}
          </h2>
          <p className="text-sm text-gray-600" data-testid="password-admin-only-note">
            {t(
              'لأسباب أمنية، لا يمكن تغيير كلمة مرور حسابات الأطباء والصيادلة. يرجى التواصل مع الإدارة لتعيين كلمة مرور جديدة.',
              'For security reasons, staff accounts cannot change their own password. Please contact the administrator to set a new one.',
              'Pour des raisons de sécurité, contactez l’administrateur pour un nouveau mot de passe.',
            )}
          </p>
        </Card>

        <div className="mt-6">
          <NotificationPreferences />
        </div>
      </div>
    </div>
  );
};
